import { createEffect, createEvent, createStore, sample } from 'effector';
import { apiClient } from '../api/client';
import { coreEnv } from '../env';
import { initKeycloakSession, keycloakLogin, keycloakLogout } from './keycloak';
import { setSessionToken } from './token-ref';
import type { LoginParams, Session } from './types';

const STORAGE_KEY = 'buildex.session';

// --- события -----------------------------------------------------------

export const logoutRequested = createEvent<void>();

// --- эффекты -----------------------------------------------------------

export const loginFx = createEffect<LoginParams, Session, Error>(async (params) => {
  const session = await apiClient.post<Session>('/auth/login', params);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  return session;
});

/** authMode=oidc: редирект на страницу входа Keycloak (PKCE, FRONTEND_GUIDE §3.1) */
export const oidcLoginFx = createEffect<void, void, Error>(() => keycloakLogin());

export const restoreSessionFx = createEffect<void, Session | null, Error>(() => {
  // oidc: сессию держит Keycloak (SSO-cookie + silent check-sso), localStorage не нужен
  if (coreEnv.authMode === 'oidc') {
    return initKeycloakSession();
  }
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
});

export const logoutFx = createEffect<void, void, Error>(async () => {
  if (coreEnv.authMode === 'oidc') {
    await keycloakLogout(); // редирект — дальше страница умирает
    return;
  }
  localStorage.removeItem(STORAGE_KEY);
  await apiClient.post<void>('/auth/logout').catch(() => undefined);
});

// --- сторы -------------------------------------------------------------

export const $session = createStore<Session | null>(null)
  .on(loginFx.doneData, (_, session) => session)
  .on(restoreSessionFx.doneData, (_, session) => session)
  .reset(logoutFx.done);

export const $isAuthenticated = $session.map((s) => s !== null);

// --- связи -------------------------------------------------------------

sample({ clock: logoutRequested, target: logoutFx });

// Токен для api-клиента (вне Effector-графа)
$session.updates.watch((session) => {
  setSessionToken(session?.token ?? null);
});

// --- права -------------------------------------------------------------

export function sessionHasPermission(session: Session | null, permission: string): boolean {
  if (session === null) return false;
  return session.permissions.includes('*') || session.permissions.includes(permission);
}
